import { Typography } from '@repo/ui';
import {
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
  Card,
  LinkButton,
} from 'components';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';

import overallCompositionSvg from 'assets/images/illustrations/improving-gut-resilience/overall-composition.svg';
import functionalRedundancySvg from 'assets/images/illustrations/improving-gut-resilience/functional-redundancy.svg';
import microbialDiversitySvg from 'assets/images/illustrations/improving-gut-resilience/microbial-diversity.svg';

import styles from './ImprovingGutResilienceCard.module.css';

type ResilienceFactor = {
  illustration: string;
  title: string;
  description: string;
};

const FACTORS: ResilienceFactor[] = [
  {
    illustration: overallCompositionSvg,
    title: 'Overall composition',
    description:
      'The balance between beneficial and unfriendly microbes in your gut.',
  },
  {
    illustration: functionalRedundancySvg,
    title: 'Functional redundancy',
    description:
      'Functions are handled by more than 1 species to protect against loss of function when species are disrupted.',
  },
  {
    illustration: microbialDiversitySvg,
    title: 'Microbial diversity',
    description:
      'There is a variety of microbes with different functions to respond to different scenarios and needs.',
  },
];

const ImprovingGutResilienceCard = () => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  const { trackEvent } = useAnalytics();

  return (
    <Card className={styles.card} variant='wrapper'>
      <div className={styles.cardInner}>
        <div className={styles.header}>
          <Typography variant='heading-m' className={styles.title}>
            Improving gut resilience
          </Typography>
          <Typography variant='body-s' className={styles.intro}>
            The great news is that resilience isn't a fixed trait – it's
            something you can actively influence. These are the 3 factors that
            determine gut resilience. Refer to your Action Plan to see what
            areas you should take action on.
          </Typography>
        </div>

        <ul className={styles.factors}>
          {FACTORS.map(({ illustration, title, description }) => (
            <li key={title} className={styles.factor}>
              <img
                src={illustration}
                alt=''
                aria-hidden='true'
                className={styles.factorIllustration}
                width={140}
                height={140}
              />
              <div className={styles.factorText}>
                <Typography variant='subheading' className={styles.factorTitle}>
                  {title}
                </Typography>
                <Typography
                  variant='caption'
                  className={styles.factorDescription}
                >
                  {description}
                </Typography>
              </div>
            </li>
          ))}
        </ul>

        {!!currentKitId && (
          <div className={styles.ctaWrapper}>
            <LinkButton
              href={`/action-plan/${currentKitId}`}
              size={ButtonSizes.M}
              color={ButtonColors.PURPLE}
              variant={ButtonVariants.SOLID}
              label='View Action Plan'
              onClick={() => trackEvent('GRS Action Plan CTA Clicked', {})}
            />
          </div>
        )}
      </div>
    </Card>
  );
};

export default ImprovingGutResilienceCard;
